import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { galleryImagesAPI, type GalleryImage } from "@/lib/db-backend-api";

const GalleryCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [section, setSection] = useState({ title: "Photo Gallery", description: "Explore our beautiful collection" });

  useEffect(() => {
    const loadData = async () => {
      try {
        const imageData = await galleryImagesAPI.getAll();
        setImages(imageData);
      } catch (error) {
        console.error('Failed to load gallery images:', error);
      }
    };

    loadData();

    // Listen for updates from admin panel
    window.addEventListener('contentUpdated', loadData);
    return () => window.removeEventListener('contentUpdated', loadData);
  }, []);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section id="gallery" className="py-20 px-4 bg-background">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {section.title}
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto mb-8"></div>
          <p className="text-lg text-muted-foreground">
            {section.description}
          </p>
        </div>

        {images.length > 0 && (
          <div className="relative">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images[currentIndex].image}
                alt={images[currentIndex].alt_text || `Gallery image ${currentIndex + 1}`}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
            </div>

          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-primary" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          >
            <ChevronRight className="w-6 h-6 text-primary" />
          </button>

          <div className="flex justify-center gap-2 mt-6">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-primary w-8"
                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
              />
            ))}
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default GalleryCarousel;
